import { useState, useCallback, useRef, useEffect } from 'react';
import { Link, router, usePage } from '@inertiajs/react';
import { useTranslation } from 'react-i18next';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
} from '@dnd-kit/core';
import TeacherShell, { TeacherPageHeader } from '../../../components/layout/TeacherShell';
import { Card, Button, Input } from '../../../components/ui';
import { EmptyState } from '../../../components/layout/AppShell';
import BookPicker from '../../../components/BookPicker';
import { cn } from '../../../lib/cn';
import { ExternalLink } from 'lucide-react';

const ACCEPTED_EXTENSIONS = ['.epub', '.pdf', '.pptx'];
const ACCEPTED_MIME_TYPES = '.epub,.pdf,.pptx,application/epub+zip,application/pdf,application/vnd.openxmlformats-officedocument.presentationml.presentation';

const STATUS_COLORS = {
  draft: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  planned: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  ready: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  active: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  completed: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400',
  archived: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400',
};

export default function CoursesShow({ course, units, books, available_books = [], enrollments }) {
  const { t } = useTranslation();
  const [activeBook, setActiveBook] = useState(null);
  const [isCreatingUnit, setIsCreatingUnit] = useState(false);
  const [uploading, setUploading] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    })
  );

  const handleDragStart = useCallback((event) => {
    const book = books?.find(b => b.id === event.active.id);
    setActiveBook(book || null);
  }, [books]);

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    setActiveBook(null);

    if (over?.id === 'unit-drop-zone' && active.id) {
      const book = books?.find(b => b.id === active.id);
      if (book && !units.some(u => u.library_item_id === book.id)) {
        setIsCreatingUnit(true);
        router.post(`/teacher/courses/${course.id}/units`, {
          unit: { title: book.title, library_item_id: book.id }
        }, {
          onFinish: () => setIsCreatingUnit(false),
        });
      }
    }
  }, [books, units, course.id]);

  // Poll when there are processing books
  const hasProcessing = books?.some(b => b.status === 'processing') || uploading;
  useEffect(() => {
    if (!hasProcessing) return;
    const interval = setInterval(() => {
      router.reload({ only: ['books'], preserveScroll: true });
    }, 3000);
    return () => clearInterval(interval);
  }, [hasProcessing]);

  const handleUpload = useCallback((file) => {
    if (!file || uploading) return;
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!ACCEPTED_EXTENSIONS.includes(ext)) {
      alert('Please select an EPUB, PDF, or PPTX file');
      return;
    }
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', file.name.replace(/\.(epub|pdf|pptx)$/i, ''));
    router.post(`/teacher/courses/${course.id}/add_book`, formData, {
      forceFormData: true,
      onFinish: () => setUploading(false),
    });
  }, [uploading, course.id]);

  const handleRemoveBook = useCallback((libraryItemId) => {
    router.delete(`/teacher/courses/${course.id}/remove_book`, {
      data: { library_item_id: libraryItemId },
      preserveScroll: true,
    });
  }, [course.id]);

  const hasBooks = books && books.length > 0;

  return (
    <TeacherShell scrollTitle={<><Link href="/teacher/courses" className="hover:text-blue-600 dark:hover:text-blue-400">{t('teacherCourses.title')}</Link>{'  ›  '}{course.title}</>}>
      <TeacherPageHeader
        title={course.title}
        subtitle={course.description || 'No description'}
        backLink="/teacher/courses"
        backLabel={t('teacherCourseShow.backToCourses')}
        actions={
          <div className="flex gap-2">
            <Link href={`/teacher/courses/${course.id}/vocabulary`}>
              <Button variant="secondary" size="sm">{t('nav.vocabulary')}</Button>
            </Link>
            <Link href={`/teacher/courses/${course.id}/edit`}>
              <Button variant="secondary" size="sm">{t('teacherCourseShow.editCourse')}</Button>
            </Link>
          </div>
        }
      />

      {/* Course Meta */}
      <div className="flex items-center gap-4 mb-8 flex-wrap">
        <span className={cn('text-xs font-medium px-2 py-0.5 rounded', STATUS_COLORS[course.status] || STATUS_COLORS.draft)}>
          {course.status}
        </span>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {course.student_count} {course.student_count === 1 ? t('common.student') : t('common.students')} {t('teacherCourseShow.studentsEnrolled')}
        </span>
        {course.date_range_display && (
          <span className="inline-flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            {course.date_range_display}
          </span>
        )}
      </div>

      {/* Students */}
      <StudentsSection enrollments={enrollments} courseId={course.id} />

      <DndContext
        sensors={sensors}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        {/* Books */}
        <BooksSection
          books={books || []}
          availableBooks={available_books}
          units={units}
          courseId={course.id}
          onUpload={handleUpload}
          onRemoveBook={handleRemoveBook}
          uploading={uploading}
        />

        {/* Units */}
        <UnitsSection
          units={units}
          courseId={course.id}
          showDropZone={hasBooks}
          isCreatingUnit={isCreatingUnit}
        />

        <DragOverlay>
          {activeBook ? <BookCardOverlay book={activeBook} /> : null}
        </DragOverlay>
      </DndContext>
    </TeacherShell>
  );
}

/* ── Books Section ── */

function BooksSection({ books, availableBooks = [], units, courseId, onUpload, onRemoveBook, uploading }) {
  const { t } = useTranslation();
  const [dragOver, setDragOver] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerSelected, setPickerSelected] = useState([]);
  const [attaching, setAttaching] = useState(false);
  const fileInputRef = useRef(null);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) onUpload(file);
  }, [onUpload]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const handleAttachSelected = useCallback(() => {
    if (pickerSelected.length === 0) return;
    setAttaching(true);
    router.post(
      `/teacher/courses/${courseId}/attach_book`,
      { library_item_ids: pickerSelected },
      {
        preserveScroll: true,
        onFinish: () => {
          setAttaching(false);
          setPickerSelected([]);
          setPickerOpen(false);
        },
      }
    );
  }, [pickerSelected, courseId]);

  const readyBooks = books.filter(b => b.status === 'ready');
  const processingBooks = books.filter(b => b.status === 'processing');
  const canAttach = availableBooks.length > 0;

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          {t('teacherCourseShow.books')} ({readyBooks.length})
        </h2>
        {canAttach && (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setPickerOpen(open => !open)}
          >
            {pickerOpen ? 'Cancel' : 'Add from library'}
          </Button>
        )}
      </div>

      {pickerOpen && canAttach && (
        <div className="mb-4 p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40">
          <BookPicker
            books={availableBooks}
            selectedIds={pickerSelected}
            onChange={setPickerSelected}
          />
          <div className="flex justify-end mt-3">
            <Button
              size="sm"
              onClick={handleAttachSelected}
              disabled={pickerSelected.length === 0 || attaching}
            >
              {attaching
                ? 'Adding…'
                : `Add ${pickerSelected.length || ''} ${pickerSelected.length === 1 ? 'book' : 'books'}`.trim()}
            </Button>
          </div>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_MIME_TYPES}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(f); e.target.value = ''; }}
        className="hidden"
        disabled={uploading}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {books.map((book) => (
          <DraggableBookCard
            key={book.id}
            book={book}
            isAssigned={units.some(u => u.library_item_id === book.id)}
            onRemove={() => onRemoveBook(book.id)}
          />
        ))}

        {/* Upload drop zone — always visible as last grid item */}
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => fileInputRef.current?.click()}
          className={cn(
            'rounded-lg border-2 border-dashed transition-all cursor-pointer min-h-[88px]',
            'flex flex-col items-center justify-center gap-2 p-4',
            dragOver
              ? 'border-blue-500 bg-blue-50 dark:border-blue-400 dark:bg-blue-900/20'
              : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500',
            uploading && 'animate-pulse pointer-events-none'
          )}
        >
          {uploading ? (
            <>
              <Spinner className="w-5 h-5 text-gray-400" />
              <span className="text-xs text-gray-500 dark:text-gray-400">{t('common.uploading')}</span>
            </>
          ) : (
            <>
              <UploadIcon className="w-5 h-5 text-gray-400 dark:text-gray-500" />
              <span className="text-xs text-gray-500 dark:text-gray-400 text-center">
                {books.length === 0 ? t('teacherCourseShow.dropFileHere') : t('teacherCourseShow.addAnotherBook')}
              </span>
            </>
          )}
        </div>
      </div>

      {readyBooks.length > 0 && (
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
          {t('teacherCourseShow.dragBookToCreate')}
        </p>
      )}
    </div>
  );
}

function DraggableBookCard({ book, isAssigned, onRemove }) {
  const { t } = useTranslation();
  const isReady = book.status === 'ready';
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: book.id,
    disabled: isAssigned || !isReady,
  });

  return (
    <div
      ref={setNodeRef}
      {...(isReady ? listeners : {})}
      {...(isReady ? attributes : {})}
      className={cn(
        'block relative group',
        isReady && 'touch-none',
        isDragging && 'opacity-30',
        isAssigned && 'opacity-50 cursor-not-allowed',
        !isAssigned && isReady && !isDragging && 'cursor-grab active:cursor-grabbing'
      )}
    >
      <Card className="p-4 hover:shadow-md transition-shadow h-full">
        <div className="flex items-start gap-3">
          {book.cover_image_url ? (
            <img
              src={book.cover_image_url}
              alt=""
              className="w-12 h-16 object-cover rounded flex-shrink-0"
            />
          ) : (
            <BookPlaceholder size="lg" />
          )}
          <div className="min-w-0 flex-1">
            <p className="font-medium text-gray-900 dark:text-white truncate">{book.title}</p>
            {book.author && (
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">{book.author}</p>
            )}
            {book.status === 'processing' ? (
              <div className="flex items-center gap-2 mt-1.5">
                <Spinner className="w-3 h-3 text-gray-400" />
                <span className="text-xs text-gray-400">{t('common.processing')}</span>
              </div>
            ) : (
              <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400 mt-1.5">
                {book.chapter_count > 0 && <span>{book.chapter_count} {t('teacherCourseShow.chapters')}</span>}
                {book.chunk_count > 0 && <span>{book.chunk_count} {t('teacherCourseShow.passages')}</span>}
              </div>
            )}
            {isAssigned && (
              <span className="inline-block mt-1.5 text-xs text-green-600 dark:text-green-400">
                {t('teacherCourseShow.assignedToUnit')}
              </span>
            )}
          </div>
        </div>
      </Card>
      {/* Open in Library — visible on hover; placed bottom-right so it doesn't
          collide with the top-right remove button. onPointerDown stops the
          event before @dnd-kit's listeners pick it up as a drag start. */}
      {isReady && (
        <Link
          href={`/teacher/library/${book.id}`}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          className="absolute bottom-2 right-2 inline-flex items-center gap-1 text-[11px] font-medium px-1.5 py-0.5 rounded-md opacity-0 group-hover:opacity-100 transition-opacity text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 cursor-pointer"
          title="Open in Library"
        >
          <ExternalLink className="w-3 h-3" strokeWidth={1.75} />
          Open
        </Link>
      )}
      {/* Remove button — visible on hover */}
      {!isAssigned && (
        <button
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          onPointerDown={(e) => e.stopPropagation()}
          className="absolute top-2 right-2 p-1 rounded-md opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-red-500 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700"
          title="Remove from course"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );
}

function BookCardOverlay({ book }) {
  return (
    <div className="w-64 opacity-90 shadow-2xl rotate-2 pointer-events-none">
      <Card className="p-4 border-2 border-blue-500 dark:border-blue-400">
        <p className="font-medium text-gray-900 dark:text-white truncate">{book.title}</p>
        {book.author && (
          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{book.author}</p>
        )}
      </Card>
    </div>
  );
}

function BookPlaceholder({ size = 'lg' }) {
  const sizeClasses = size === 'lg' ? 'w-12 h-16' : 'w-8 h-11';
  const iconSize = size === 'lg' ? 'w-6 h-6' : 'w-4 h-4';
  return (
    <div className={cn(sizeClasses, 'rounded bg-gray-200 dark:bg-gray-700 flex items-center justify-center flex-shrink-0')}>
      <svg className={cn(iconSize, 'text-gray-400 dark:text-gray-500')} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
      </svg>
    </div>
  );
}

/* ── Units Section ── */

function UnitsSection({ units, courseId, showDropZone, isCreatingUnit }) {
  const { t } = useTranslation();
  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          Units ({units.length})
        </h2>
        <Link href={`/teacher/courses/${courseId}/units/new`}>
          <Button variant="primary" size="sm">
            <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {t('teacherCourseShow.addUnit')}
          </Button>
        </Link>
      </div>

      {showDropZone && <UnitDropZone isCreatingUnit={isCreatingUnit} />}

      {units.length === 0 && !showDropZone ? (
        <EmptyState
          title={t('teacherCourseShow.noUnits')}
          description={t('teacherCourseShow.noUnitsHelp')}
          action={
            <Link href={`/teacher/courses/${courseId}/units/new`}>
              <Button>{t('teacherCourseShow.addAUnit')}</Button>
            </Link>
          }
        />
      ) : units.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
          {t('teacherCourseShow.noUnitsDrag')}
        </p>
      ) : (
        <div className="space-y-4">
          {units.map((unit, index) => (
            <UnitSection key={unit.id} unit={unit} index={index} courseId={courseId} />
          ))}
        </div>
      )}
    </div>
  );
}

function UnitDropZone({ isCreatingUnit }) {
  const { t } = useTranslation();
  const { isOver, setNodeRef } = useDroppable({ id: 'unit-drop-zone' });

  return (
    <div
      ref={setNodeRef}
      className={cn(
        'w-full p-6 mb-4 rounded-lg border-2 border-dashed transition-all',
        'flex items-center justify-center gap-2 text-sm font-medium',
        isOver
          ? 'border-blue-500 bg-blue-50 text-blue-700 dark:border-blue-400 dark:bg-blue-900/20 dark:text-blue-300'
          : 'border-gray-300 text-gray-500 dark:border-gray-600 dark:text-gray-400',
        isCreatingUnit && 'animate-pulse'
      )}
    >
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
      </svg>
      {isCreatingUnit ? t('teacherCourseShow.creatingUnit') : t('teacherCourseShow.dropBookHere')}
    </div>
  );
}

/* ── Unit Card ── */

function UnitSection({ unit, index, courseId }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(true);
  const lessons = unit.lessons || [];

  return (
    <Card className="overflow-hidden">
      {/* Unit header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
      >
        <div className="flex items-center gap-4">
          <span className="text-sm font-mono text-gray-400 dark:text-gray-500 w-8 text-center">
            {index + 1}
          </span>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white">{unit.title}</h3>
            {unit.book_title && (
              <span className="inline-flex items-center gap-1 mt-1 text-xs text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 px-2 py-0.5 rounded">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                {unit.book_title}
              </span>
            )}
            {unit.description && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{unit.description}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {lessons.length} {lessons.length === 1 ? t('common.lesson') : t('common.lessons')}
          </span>
          <svg
            className={cn('w-5 h-5 text-gray-400 transition-transform', expanded && 'rotate-180')}
            fill="none" stroke="currentColor" viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Lessons within unit */}
      {expanded && (
        <div className="border-t border-gray-200 dark:border-gray-700">
          {/* Plan Chapters link for book-based units */}
          {unit.library_item_id && (
            <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center gap-3">
              <Link href={`/teacher/courses/${courseId}/units/${unit.id}/plan`}>
                <Button variant="secondary" size="sm">{t('teacherCourseShow.planChapters')}</Button>
              </Link>
              <span className="text-xs text-gray-400 dark:text-gray-500">
                {t('teacherCourseShow.assignChapters')}
              </span>
            </div>
          )}

          {lessons.length === 0 ? (
            <div className="px-5 py-4 text-center">
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">{t('teacherCourseShow.noLessons')}</p>
              <Link href={`/teacher/courses/${courseId}/units/${unit.id}/lessons/new`}>
                <Button variant="secondary" size="sm">{t('teacherCourseShow.addLesson')}</Button>
              </Link>
            </div>
          ) : (
            <>
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {lessons.map((lesson, lIndex) => (
                  <Link
                    key={lesson.id}
                    href={`/teacher/courses/${courseId}/units/${unit.id}/lessons/${lesson.id}`}
                    className="block px-5 py-3 hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <span className="text-xs font-mono text-gray-400 dark:text-gray-500 w-6 text-center">
                          {lIndex + 1}
                        </span>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">{lesson.title}</p>
                          <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            <span className="capitalize">{lesson.lesson_type.replace('_', ' ')}</span>
                            {lesson.chapter_range && <span>{lesson.chapter_range}</span>}
                            <span>{lesson.activity_count} {lesson.activity_count === 1 ? t('common.activity') : t('common.activities')}</span>
                            {lesson.duration_minutes && <span>{lesson.duration_minutes} {t('common.min')}</span>}
                          </div>
                        </div>
                      </div>
                      <span className={cn('text-xs font-medium px-2 py-0.5 rounded', STATUS_COLORS[lesson.status] || STATUS_COLORS.draft)}>
                        {lesson.status}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
              <div className="px-5 py-3 border-t border-gray-100 dark:border-gray-700">
                <Link href={`/teacher/courses/${courseId}/units/${unit.id}/lessons/new`}>
                  <Button variant="ghost" size="sm">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    {t('teacherCourseShow.addLessonShort')}
                  </Button>
                </Link>
              </div>
            </>
          )}
        </div>
      )}
    </Card>
  );
}

/* ── Students Section ── */

function StudentsSection({ enrollments, courseId }) {
  const { t } = useTranslation();
  const { auth } = usePage().props;
  const isAdmin = auth?.user?.role === 'admin';
  const userPath = (userId) => isAdmin ? `/admin/users/${userId}` : `/teacher/users/${userId}`;

  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (value) => {
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (value.trim().length < 2) {
      setResults([]);
      setShowDropdown(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setIsSearching(true);
      try {
        const res = await fetch(`/teacher/courses/${courseId}/enrollments/search?q=${encodeURIComponent(value)}`);
        const data = await res.json();
        setResults(data);
        setShowDropdown(true);
      } finally {
        setIsSearching(false);
      }
    }, 300);
  };

  const handleEnroll = (userId) => {
    setQuery('');
    setResults([]);
    setShowDropdown(false);
    router.post(`/teacher/courses/${courseId}/enrollments`, { user_id: userId });
  };

  const handleRemove = (enrollmentId) => {
    router.delete(`/teacher/courses/${courseId}/enrollments/${enrollmentId}`);
  };

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          Students ({enrollments.length})
        </h2>
      </div>

      {/* Search to add */}
      <div className="relative mb-4" ref={dropdownRef}>
        <Input
          value={query}
          onChange={(e) => handleSearch(e.target.value)}
          placeholder={t('teacherCourseShow.searchStudents')}
        />
        {isSearching && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
          </div>
        )}

        {showDropdown && results.length > 0 && (
          <div className="absolute z-20 w-full mt-1 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 max-h-60 overflow-y-auto">
            {results.map((student) => (
              <button
                key={student.id}
                onClick={() => handleEnroll(student.id)}
                className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-left"
              >
                <div className={cn(
                  'w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium',
                  'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                )}>
                  {student.initials}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{student.full_name}</p>
                  {student.email && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{student.email}</p>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}

        {showDropdown && query.trim().length >= 2 && results.length === 0 && !isSearching && (
          <div className="absolute z-20 w-full mt-1 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-3 px-4 text-sm text-gray-500 dark:text-gray-400">
            {t('teacherCourseShow.noStudentsFound')}
          </div>
        )}
      </div>

      {/* Enrolled list */}
      {enrollments.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('teacherCourseShow.noStudentsEnrolled')}
        </p>
      ) : (
        <Card padding="none">
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {enrollments.map((enrollment) => (
              <div key={enrollment.id} className="flex items-center justify-between px-5 py-3">
                <Link
                  href={userPath(enrollment.user_id)}
                  className="flex items-center gap-3 min-w-0 hover:opacity-80 transition-opacity"
                >
                  <div className={cn(
                    'w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium',
                    'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                  )}>
                    {enrollment.initials}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{enrollment.full_name}</p>
                    {enrollment.email && (
                      <p className="text-xs text-gray-500 dark:text-gray-400">{enrollment.email}</p>
                    )}
                  </div>
                </Link>
                <button
                  onClick={() => handleRemove(enrollment.id)}
                  className="p-1.5 text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                  title={t('teacherCourseShow.removeStudent')}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

/* ── Icons ── */

function Spinner({ className }) {
  return (
    <svg className={cn('animate-spin', className)} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
    </svg>
  );
}

function UploadIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
    </svg>
  );
}
